/* eslint-disable max-len */
import { assignValueToColorBin } from '@local/finsera-core/src/helpers';
import { _, am5, am5hierarchy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { FONT_SIZE } from '../../../../theme/layout-size';

export const createSeries = ({
  root,
  container,
  data,
  category,
  homeText,
  lvl1Name,
  lvl1Value,
  lvl2Name,
  lvl2Value,
  lvl2Weight,
  lvl2Label,
  colors,
  withSign,
  colorBoundaries,
  childBoundaries,
}: {
  root: am5.Root;
  container: am5.Container;
  data: types.widgets.TableData;
  category: string;
  homeText: string;
  lvl1Name: string;
  lvl1Value: string;
  lvl2Name: string;
  lvl2Value: string;
  lvl2Weight: string;
  lvl2Label: string;
  colors: number[][];
  withSign: boolean;
  colorBoundaries: {
    minValue: number;
    maxValue: number;
  };
  childBoundaries: Record<string, { minValue: number; maxValue: number }>;
}) => {
  // Create and configure the treemap series with initial settings
  const series = container.children.push(
    am5hierarchy.Treemap.new(root, {
      maskContent: false,
      sort: 'descending',
      singleBranchOnly: false,
      downDepth: 1,
      upDepth: 0,
      initialDepth: 1,
      valueField: 'value',
      categoryField: 'name',
      childDataField: 'children',
      nodePaddingOuter: 0,
      nodePaddingInner: 0,
      layoutAlgorithm: 'squarify',
    })
  );

  // Group data by category
  const groupedData = _.groupBy(data, category);

  // Configure node fill colors based on depth and values
  series.rectangles.template.adapters.add('fill', (fill, target) => {
    const customColor = (target.dataItem.dataContext as any).color;
    return customColor || fill;
  });

  // Set node opacity based on depth
  series.rectangles.template.adapters.add('fillOpacity', (opacity, target) => {
    const dataItem = target.dataItem;
    if (dataItem) {
      const depth = dataItem.get('depth' as any);
      return depth === 1 || depth === 2 ? 1 : 0;
    }
    return 0;
  });

  // Transform flat data into hierarchical structure for treemap
  const transformedData = [
    {
      name: homeText || 'Root',
      children: Object.entries(groupedData).map(([categoryName, items]) => {
        const parentData = items[0];
        const value = Number(parentData[lvl1Value]);
        const sign = value * 100 >= 0 ? '+' : '';
        const formattedValue = root.numberFormatter.format(value);
        const colorIndex = assignValueToColorBin(value, colorBoundaries, colors.length - 1);

        return {
          name: categoryName,
          tooltipName: parentData[lvl1Name],
          color: am5.color(`rgb(${colors[colorIndex][0]}, ${colors[colorIndex][1]}, ${colors[colorIndex][2]})`),
          info: withSign ? `${sign}${formattedValue}` : `${formattedValue}`,
          children: items
            .filter((item) => item[lvl2Label] !== null)
            .map((item) => {
              const value = Number(item[lvl2Value]);
              const sign = value * 100 >= 0 ? '+' : '';
              const formattedValue = root.numberFormatter.format(value);
              const childrenColorIndex = assignValueToColorBin(value, childBoundaries[categoryName], colors.length - 1);

              return {
                name: item[lvl2Label],
                value: item[lvl2Weight],
                color: am5.color(
                  `rgb(${colors[childrenColorIndex][0]}, ${colors[childrenColorIndex][1]}, ${colors[childrenColorIndex][2]})`
                ),
                info: withSign ? `${sign}${formattedValue}` : `${formattedValue}`,
                tooltipName: item[lvl2Name],
              };
            }),
        };
      }),
    },
  ];

  // Configure node labels with name and value information
  series.labels.template.adapters.add('text', (text, target) => {
    const dataItem = target.dataItem;
    if (dataItem) {
      const name = (dataItem.dataContext as any).name;
      const info = (dataItem.dataContext as any).info;
      return `${name}\n${info}`;
    }
    return text;
  });

  // Add padding to the labels inside nodes
  series.labels.template.setAll({
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 6,
    paddingBottom: 6,
  });

  // Configure tooltips to only show for leaf nodes
  series.nodes.template.set('tooltipText', '');
  series.nodes.template.adapters.add('tooltipText', (text, target) => {
    const depth = target.dataItem?.get('depth' as any);
    if (depth !== 2) {
      return '';
    }
    const tooltipName = (target.dataItem?.dataContext as any).tooltipName;
    const info = (target.dataItem?.dataContext as any).info;
    return `[bold]${tooltipName}[/]: ${info}`;
  });

  // Initialize the chart with data
  series.data.setAll(transformedData);
  series.set('selectedDataItem', series.dataItems[0]);

  const backButton = container.children.push(
    am5.Button.new(root, {
      dx: 5,
      dy: 30,
      layer: 40,
      cursorOverStyle: 'pointer',
      visible: false,

      icon: am5.Graphics.new(root, {
        centerX: am5.p50,
        centerY: am5.p50,
        fill: am5.color('#ffffff'),
        svgPath:
          'M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z',
      }),
      background: am5.RoundedRectangle.new(root, {
        fill: am5.color('#1B1F37'),
        fillOpacity: 1,
        cornerRadiusTL: 4,
        cornerRadiusTR: 4,
        cornerRadiusBL: 4,
        cornerRadiusBR: 4,
      }),
    })
  );

  backButton.get('background').states.create('hover', {
    fillOpacity: 0.8,
  });

  let buttonVisible = false;

  backButton.events.on('click', () => {
    const currentDataItem = series.get('selectedDataItem');
    if (currentDataItem && currentDataItem.get('parent')) {
      buttonVisible = false;
      series.set('selectedDataItem', currentDataItem.get('parent'));
      backButton.set('visible', false);
    }
  });

  backButton.on('visible', (isVisible) => {
    if (isVisible !== buttonVisible) {
      backButton.set('visible', buttonVisible);
    }
  });

  series.events.on('dataitemselected', (ev) => {
    const selectedDataItem = ev.target.get('selectedDataItem');
    buttonVisible = selectedDataItem && selectedDataItem.get('depth') > 0;
    backButton.set('visible', selectedDataItem && selectedDataItem.get('depth') > 0);
  });

  const breadcrumbBar = am5hierarchy.BreadcrumbBar.new(root, {
    series: series,
  });

  breadcrumbBar.labels.template.setAll({
    fontSize: FONT_SIZE.xxs,
    fill: am5.color('#ffffff'),
    fontWeight: '600',
    textAlign: 'left',
    layer: 30,
    paddingLeft: 0,
    paddingRight: 4,
    paddingTop: 4,
    paddingBottom: 4,
  });

  breadcrumbBar.set(
    'background',
    am5.Rectangle.new(root, {
      fill: am5.color('#1B1F37'),
    })
  );

  container.children.push(breadcrumbBar);
};
